/**
 * Shared utilities for slot rendering
 */

import {SliderKeyboardOperation} from '@wandb/weave/common/util/media';

import {useDetectPanelVariant} from '../../../Panel/hooks/hooks';
import {type MediaConfigWithDefaults} from '../../Component';
import {type LayoutSlot, type SlotRegistry} from '../types';

// ============================================================================
// SlotRegistry Initialization
// ============================================================================

/**
 * All available layout slots in order.
 * Used to initialize empty SlotRegistry objects.
 */
export const ALL_LAYOUT_SLOTS: readonly LayoutSlot[] = [
  'contentHeader',
  'contentFooter',
  'contentFooterAction',
  'columnHeader',
  'columnFooter',
  'rowHeader',
  'rowFooter',
  'tileHeader',
  'tileFooter',
] as const;

/**
 * Creates an empty SlotRegistry with all slots initialized to empty arrays.
 * Use this to avoid manually typing out each slot name.
 *
 * @example
 * const slots = createEmptySlotRegistry();
 * slots['tile-header'].push(label('run'));
 */
export function createEmptySlotRegistry(): SlotRegistry {
  return ALL_LAYOUT_SLOTS.reduce((acc, slot) => {
    acc[slot] = [];
    return acc;
  }, {} as SlotRegistry);
}

// ============================================================================
// Slot Rendering Hooks
// ============================================================================

/**
 * Hook that returns keyboard bindings for step slider when in full screen mode.
 *
 * Only includes bindings when panel is viewed in full screen to avoid
 * conflicts with global keyboard shortcuts.
 *
 * @returns Keyboard bindings object or undefined if not in full screen
 */
export const useStepSliderKeyboardBindings = () => {
  const {isFullScreenPanelPage} = useDetectPanelVariant();

  return isFullScreenPanelPage
    ? {
        'Meta+ArrowRight': SliderKeyboardOperation.INCREMENT,
        'Meta+ArrowLeft': SliderKeyboardOperation.DECREMENT,
        'Control+ArrowRight': SliderKeyboardOperation.INCREMENT,
        'Control+ArrowLeft': SliderKeyboardOperation.DECREMENT,
      }
    : undefined;
};

/**
 * Hook that returns keyboard bindings for index slider when in full screen mode.
 *
 * Only includes bindings when panel is viewed in full screen to avoid
 * conflicts with global keyboard shortcuts.
 *
 * @returns Keyboard bindings object or undefined if not in full screen
 */
export const useIndexSliderKeyboardBindings = () => {
  const {isFullScreenPanelPage} = useDetectPanelVariant();

  return isFullScreenPanelPage
    ? {
        'Alt+ArrowRight': SliderKeyboardOperation.INCREMENT,
        'Alt+ArrowLeft': SliderKeyboardOperation.DECREMENT,
      }
    : undefined;
};

/**
 * Determines which axes are currently active based on the config mode.
 *
 * @param config - Media panel configuration
 * @returns Array of active axis names (e.g., ['step'], ['step', 'run'])
 */
export const getActiveAxes = (config: MediaConfigWithDefaults): string[] => {
  const mode = config.mode ?? 'gallery';
  if (mode === 'compare') {
    return [];
  }
  const galleryAxis = config.gallerySettings?.axis ?? 'step';
  const gridXAxis = config.gridSettings?.xAxis ?? 'step';
  const gridYAxis = config.gridSettings?.yAxis ?? 'run';

  return mode === 'gallery' ? [galleryAxis] : [gridXAxis, gridYAxis];
};
